import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, AlertCircle, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, password });
      setSuccess(true);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="reset-password-page">
        <div className="text-center">
          <p className="text-text-secondary font-body mb-4">Invalid reset link. No token provided.</p>
          <Link to="/forgot-password" className="text-white underline text-sm">Request a new reset link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        {success ? (
          <div data-testid="reset-password-success">
            <div className="w-14 h-14 mb-6 flex items-center justify-center bg-available/10 border border-available/30">
              <Check size={24} className="text-available" />
            </div>
            <h2 className="font-heading text-2xl font-medium tracking-tight text-white mb-2">Password updated</h2>
            <p className="text-text-secondary text-sm font-body mb-6">Your password has been reset. You can now sign in.</p>
            <Link
              to="/login"
              data-testid="reset-go-to-login"
              className="inline-block px-5 py-2.5 bg-white text-black text-sm font-body font-medium hover:opacity-90 transition-all"
            >
              Sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <h2 className="font-heading text-2xl font-medium tracking-tight text-white" data-testid="reset-password-heading">
                Set new password
              </h2>
              <p className="text-text-secondary text-sm mt-2 font-body">Choose a strong password for your account.</p>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 mb-6 border border-booked/30 bg-booked/10 text-sm text-booked" data-testid="reset-password-error">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-mono text-text-secondary block mb-2">New password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    data-testid="reset-password-input"
                    required
                    minLength={6}
                    className="w-full bg-surface border border-border text-white pl-10 pr-4 py-2.5 text-sm font-body placeholder:text-text-secondary/50 focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition-all"
                    placeholder="Min 6 characters"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-mono text-text-secondary block mb-2">Confirm password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                  <input
                    type="password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    data-testid="reset-password-confirm-input"
                    required
                    className="w-full bg-surface border border-border text-white pl-10 pr-4 py-2.5 text-sm font-body placeholder:text-text-secondary/50 focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition-all"
                    placeholder="Repeat password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="reset-password-submit-button"
                className="w-full bg-white text-black font-body font-medium text-sm py-2.5 flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all duration-200 disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                ) : (
                  'Reset password'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
